<script lang="ts" setup>
import type { ConsiderationColor } from '~kui/utils/considerationColor';

import { Card } from '~ds/components/ui/card';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

interface Props {
  color?: ConsiderationColor;
  title: string;
  level: number;
  illustration?: string;
  progress?: number;
  showProgressPercentage?: boolean;
  animateLevelUp?: boolean;
  to?: string;
}

const props = withDefaults(defineProps<Props>(), {
  color: 'lime',
  illustration: undefined,
  progress: 0,
  showProgressPercentage: true,
  animateLevelUp: false,
  to: undefined,
});

const { t } = useI18n();

const colorVariantClasses = computed(() =>
  twMerge(
    cva('', {
      variants: {
        color: {
          lime: 'bg-secondary',
          coral: 'bg-brand-coral',
          pink: 'bg-brand-pink',
          purple: 'bg-brand-purple',
          orange: 'bg-brand-orange',
          blue: 'bg-brand-blue',
          yellow: 'bg-brand-yellow',
        },
      },
    })({ color: props.color }),
  ),
);

const pillClasses = computed(() =>
  twMerge(
    cva('flex shrink-0 items-center gap-0.5 whitespace-nowrap px-1.5 py-1 rounded-sm', {
      variants: {
        color: {
          lime: 'bg-secondary-subtle text-foreground',
          coral: 'bg-brand-coral-subtle text-foreground',
          pink: 'bg-brand-pink-subtle text-foreground',
          purple: 'bg-brand-purple-subtle text-foreground',
          orange: 'bg-brand-orange-subtle text-foreground',
          blue: 'bg-brand-blue-subtle text-foreground',
          yellow: 'bg-brand-yellow-subtle text-foreground',
        },
      },
    })({ color: props.color }),
  ),
);

// White reads on the two deeper colours, as in the design; the light ones need the dark ink.
const arrowClasses = computed(() =>
  twMerge(
    cva('flex size-6 shrink-0 items-center justify-center rounded-full shadow-sm transition-transform group-hover:translate-x-0.5', {
      variants: {
        color: {
          lime: 'bg-secondary text-foreground',
          coral: 'bg-brand-coral text-white',
          pink: 'bg-brand-pink text-foreground',
          purple: 'bg-brand-purple text-white',
          orange: 'bg-brand-orange text-foreground',
          blue: 'bg-brand-blue text-foreground',
          yellow: 'bg-brand-yellow text-foreground',
        },
      },
    })({ color: props.color }),
  ),
);

const remainingPercentage = computed(() => Math.max(0, 100 - Math.round(props.progress)));

const STAR_SPARKLES = Array.from({ length: 6 }, (_, i) => {
  const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
  return {
    id: i,
    x: Math.round(Math.cos(angle) * 18),
    y: Math.round(Math.sin(angle) * 18),
    delay: (i % 3) * 0.04,
  };
});

const burstCount = ref(0);

watch(
  () => props.level,
  (newLevel, oldLevel) => {
    if (props.animateLevelUp && newLevel > oldLevel) {
      burstCount.value += 1;
    }
  },
);

const starPop = computed(() =>
  burstCount.value === 0
    ? { scale: 1 }
    : { scale: [1, 1.7, 0.85, 1.15, 1], rotate: [0, 16, -8, 4, 0] },
);
</script>

<template>
  <Card
    shape="stretch"
    class="relative block gap-0 py-0"
    :class="to && 'group has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-ring has-[a:focus-visible]:ring-offset-2'"
  >
    <div class="relative h-full rounded-card overflow-hidden isolate [clip-path:inset(0_round_var(--radius-card))]">
      <div :class="colorVariantClasses" class="size-30 blur-lg rounded-full absolute -right-8 -top-12 pointer-events-none" />
      <div class="relative flex h-full flex-col justify-between gap-3 p-4">
        <div class="flex flex-col gap-2">
          <Heading
            size="md"
            class="leading-6 max-w-[60%]"
            as="h3"
          >
            <!-- The link's overlay stretches over the whole card, so the card is one target named by its title. -->
            <NuxtLink
              v-if="to"
              :to="to"
              class="outline-none after:absolute after:inset-0 after:z-10 after:content-['']"
              data-testid="progress-card-link"
            >
              {{ title }}
            </NuxtLink>
            <template v-else>
              {{ title }}
            </template>
          </Heading>
          <div class="flex flex-wrap gap-2 items-center">
            <div :class="pillClasses">
              <span class="relative inline-flex items-center justify-center">
                <motion.span
                  :key="burstCount"
                  class="inline-flex"
                  :animate="starPop"
                  :transition="{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }"
                >
                  <Icon
                    :name="ICONS.starSolid"
                    size="xs"
                  />
                </motion.span>
                <template v-if="burstCount > 0">
                  <motion.span
                    v-for="sparkle in STAR_SPARKLES"
                    :key="`${burstCount}-${sparkle.id}`"
                    class="absolute left-1/2 top-1/2 -ml-0.5 -mt-0.5 size-1 rounded-full bg-current pointer-events-none"
                    :initial="{ opacity: 0, x: 0, y: 0, scale: 0.2 }"
                    :animate="{ opacity: [0, 1, 0], x: sparkle.x, y: sparkle.y, scale: [0.2, 1, 0.3] }"
                    :transition="{ duration: 0.7, delay: sparkle.delay, ease: 'easeOut' }"
                  />
                </template>
              </span>
              <Text
                size="sm"
                weight="medium"
                class="leading-none text-foreground"
              >
                {{ t('unified.considerations.level', { level }) }}
              </Text>
            </div>
            <span v-if="remainingPercentage < 100" class="text-xs text-muted-foreground leading-none whitespace-nowrap">
              {{ t('unified.considerations.levelUntilNext', { remaining: remainingPercentage, nextLevel: level + 1 }) }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <KUICoreProgressBar
            class="flex-1"
            :progress="progress"
            :color="color"
            :show-percentage="showProgressPercentage"
          />
          <span
            v-if="to"
            aria-hidden="true"
            :class="arrowClasses"
            data-testid="progress-card-arrow"
          >
            <Icon :name="ICONS.arrowRight" size="sm" />
          </span>
        </div>
      </div>
    </div>
    <NuxtImg
      v-if="illustration"
      :src="illustration"
      alt=""
      role="presentation"
      data-testid="card-illustration"
      class="absolute -top-4 right-1 size-18 object-contain pointer-events-none"
    />
  </Card>
</template>
